import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import PageHero from "@/components/PageHero";
import ProductCard from "@/components/ProductCard";
import { FadeUp } from "@/components/Reveal";
import { IMAGES } from "@/data/content";
import SEO from "@/components/SEO";

const Products = () => {
  const [params, setParams] = useSearchParams();
  const active = params.get("crop") || "all";
  const [crops, setCrops] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/crops").then((r) => setCrops(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get("/products", { params: active === "all" ? {} : { crop: active } })
      .then((r) => setProducts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [active]);

  return (
    <>
      <SEO
        title="Hybrid Seed Portfolio"
        description="Explore 30 high-performance hybrids across 9 crop categories — hybrid cotton, maize, paddy, basmati rice, bajra, sunflower, jowar and research wheat from Seed Park India."
      />
      <PageHero
        eyebrow="Products"
        titleLines={["Hybrids engineered", "to outperform."]}
        description="Crop-wise portfolios of research-backed hybrids — each one validated across hundreds of multi-location trials before release."
        image={IMAGES.hero}
      />
      <section data-testid="products-catalogue" className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <div className="mb-12 flex flex-wrap gap-2" data-testid="crop-filter-bar">
              <button
                onClick={() => setParams({})}
                data-testid="filter-all"
                className={`border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
                  active === "all"
                    ? "border-brand-400 bg-brand-400 text-forest-950"
                    : "border-white/15 text-mist hover:border-brand-400 hover:text-brand-400"
                }`}
              >
                All Crops
              </button>
              {crops.map((c) => (
                <button
                  key={c.slug}
                  onClick={() => setParams({ crop: c.slug })}
                  data-testid={`filter-${c.slug}`}
                  className={`border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
                    active === c.slug
                      ? "border-brand-400 bg-brand-400 text-forest-950"
                      : "border-white/15 text-mist hover:border-brand-400 hover:text-brand-400"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </FadeUp>

          {loading ? (
            <p className="py-20 text-center font-mono text-xs uppercase tracking-[0.3em] text-sage" data-testid="products-loading">
              Loading portfolio…
            </p>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="products-grid">
              {products.map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Products;
